import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import style from './RichTooltip.module.scss';

export interface RichTooltipProps {
  tooltip: (() => React.ReactElement) | React.ReactElement | string | undefined;
  position?: Placement;
  backgroundColor?: string;
  className?: string;
  children?: React.ReactNode;
  childrenClassName?: string;
  fixed?: boolean;
  enabled?: boolean;
  isOpen?: boolean;
  element?: 'button' | 'div' | 'span';
  style?: React.CSSProperties;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

const DEFAULT_BACKGROUND = getComputedStyle(document.documentElement)
  .getPropertyValue('--tooltip-bg')
  .trim();

const RichTooltip: React.FC<RichTooltipProps> = ({
  tooltip,
  children,
  position = 'auto',
  fixed,
  backgroundColor = DEFAULT_BACKGROUND,
  className,
  childrenClassName,
  enabled = true,
  isOpen: isOpenFromProps,
  element,
  ...rest
}) => {
  const referenceRef = useRef(null);
  const popperRef = useRef(null);
  const [isOpenInState, setIsOpen] = useState(false);
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    document.documentElement.style.setProperty('--tooltip-bg', backgroundColor);
  }, [backgroundColor]);

  const { styles, attributes } = usePopper(referenceRef.current, popperRef.current, {
    strategy: fixed ? 'fixed' : undefined,
    placement: position,
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowRef,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  });

  const handleSetIsOpen = useCallback(() => setIsOpen(true), []);
  const handleSetIsClosed = useCallback(() => setIsOpen(false), []);

  const isOpen = isOpenFromProps ?? isOpenInState;
  const Tooltip = tooltip;
  const Element = element || 'div';
  return (
    <>
      <Element
        onMouseEnter={handleSetIsOpen}
        onMouseLeave={handleSetIsClosed}
        ref={referenceRef}
        className={childrenClassName}
        {...rest}
      >
        {children}
      </Element>

      {isOpen && enabled ? (
        <div
          ref={popperRef}
          className={classNames(style.container, className)}
          style={styles.popper}
          {...attributes.popper}
        >
          <div
            ref={ref => {
              setArrowRef(ref);
            }}
            style={styles.arrow}
            className={style.arrow}
            id="arrow"
          />
          <div style={styles.offset}>{typeof Tooltip === 'function' ? <Tooltip /> : Tooltip}</div>
        </div>
      ) : null}
    </>
  );
};

export default RichTooltip;
